import { Directive } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { DefaultEmailTrigger, EmailTrigger } from '../enum/email.trigger';
import { NotificationEmail } from '../model/notificationEmail';
import { SelectOption } from '../model/select.option';
import { User } from '../model/user';
import { Event } from '../model/event';
import { EventsService } from '../service/events.service';

@Directive()
export abstract class EmailTemplatesEditDirective {
  emailTriggers: Array<SelectOption>;
  emailTriggerVariables: Array<SelectOption> = [];
  filterForm: FormGroup;
  subject: string;
  notificationEmail: NotificationEmail = new NotificationEmail();
  subjectEmail: string;
  contentEmail: string;
  userForm: FormGroup;
  changeType = true;

  preview: {
    subject: string,
    content: string
  };

  constructor(
    private limitToChairTemplates = true,
    protected fb: FormBuilder,
    protected translateService: TranslateService,
    protected eventsService: EventsService
  ) {
    if (this.limitToChairTemplates) {
      this.emailTriggers = EmailTrigger.toSelectable();
    } else {
      this.emailTriggers = DefaultEmailTrigger.toSelectable();
    }
  }

  // Required custom methods
  abstract emailExists: boolean;
  abstract initComponent();
  abstract getEmailTemplate(trigger: EmailTrigger);
  abstract updateSelectedEmailTemplate();

  initForm(): void {
    this.filterForm = this.fb.group({
      emailTrigger: [],
      emailSubject: [],
      emailTextForm: [],
    });

    this.filterForm.valueChanges.subscribe(({ emailTrigger }) => {
      if (!emailTrigger) { this.emailTriggerVariables = []; return; }
      // QUAIS variáveis existem vem do BACKEND (fonte única — é ele quem aceita
      // ou recusa no save); a DESCRIÇÃO traduzida continua no i18n. Antes a
      // lista saía inteira do i18n, e as duas divergiram: o editor oferecia
      // variáveis que o save rejeitava (issue #2371).
      //
      // Some também o sufixo " *" que marcava as variáveis fora de uma lista
      // fixa de 7 "reais": ele existia porque o front não podia confiar na
      // própria lista. Agora pode — tudo que chega aqui é aceito pelo backend.
      this.eventsService.getAllowedEmailVariables(emailTrigger).subscribe(
        allowed => this.buildTriggerVariables(emailTrigger, allowed),
        // Backend antigo (sem o endpoint) → cai no i18n, como antes. Degrada
        // em vez de deixar o chair sem nenhuma variável para inserir.
        () => this.buildTriggerVariablesFromI18n(emailTrigger)
      );
    });
  }

  /** Monta as opções a partir da lista AUTORITATIVA do backend, buscando a
   *  descrição no i18n. Sem tradução (variável nova que o i18n ainda não
   *  descreve), exibe o próprio nome — melhor oferecer sem descrição do que
   *  esconder algo que o backend aceita. */
  private buildTriggerVariables(trigger: EmailTrigger, allowed: string[]): void {
    this.translateService.get(`admin.email-template.type.${trigger}.variables`).subscribe(types => {
      const descriptions = (types && typeof types === 'object') ? types : {};
      this.emailTriggerVariables = (allowed || []).map(name =>
        new SelectOption(name, descriptions[name] || name)
      );
    });
  }

  /** Fallback: comportamento anterior, lista vinda só do i18n. */
  private buildTriggerVariablesFromI18n(trigger: EmailTrigger): void {
    this.translateService.get(`admin.email-template.type.${trigger}.variables`).subscribe(types => {
      const descriptions = (types && typeof types === 'object') ? types : {};
      this.emailTriggerVariables = Object.keys(descriptions).map(name =>
        new SelectOption(name, descriptions[name])
      );
    });
  }

  eventNotificationEmail(includeTo = true): void {
    this.notificationEmail.subject = this.filterForm.value.emailSubject;
    this.notificationEmail.content = this.filterForm.value.emailTextForm;
    this.notificationEmail.event = null;
    this.notificationEmail.trigger = this.filterForm.value.emailTrigger;

    if (includeTo && this.notificationEmail.to) {
      this.notificationEmail.to = this.notificationEmail.to.map((user: User) => user.id);
    }
    this.updateSelectedEmailTemplate();
  }

  previewEmail(event: Event): void {
    this.changeType = false;
    this.preview = {
      subject: this.filterForm.value.emailSubject,
      content: this.filterForm.value.emailTextForm
    };
    this.replaceTemplateVariables(this.filterForm.value.emailSubject, this.filterForm.value.emailTextForm, event);
  }

  replaceTemplateVariables(subject: string, content: string, event: Event) {
    if (content !== null) {
      const domain = window.location.origin;
      const variables = this.emailTriggerVariables.map(trigger => <string>trigger.id);
      const expression = new RegExp(variables.join('|'), 'gi');

      this.translateService.get(variables.map(v => `admin.email-template.variable-defaults.${v}`)).subscribe(e => {
        const variableDefaults = Object.keys(e).map(k => {
          const key = k.split('variable-defaults.')[1];

          let value;
          this.translateService.get(`admin.email-template.variable-defaults.${key}`,
          { domain: domain, event }).subscribe(item => {
            value = item;
          });
          return { key, value };
        });

        const y = {};
        variableDefaults.forEach(item => {
          y[item.key] = item.value;
        });

        const flexRegex = /\{\{\s*([^{}]+?)\s*\}\}/g;

        if (subject !== null) {
          this.preview.subject = subject.replace(flexRegex, (matched, innerKey) => {
            const strictKey = `{{${innerKey}}}`;
            return y[strictKey] !== undefined ? y[strictKey] : matched;
          });
        }
        if (content !== null) {
          this.preview.content = content.replace(flexRegex, (matched, innerKey) => {
            const strictKey = `{{${innerKey}}}`;
            return y[strictKey] !== undefined ? y[strictKey] : matched;
          });
        }
        
        const invalidRegex = /\{\{([^{}]+?)\}\}/g;
        if (this.preview.subject) {
          this.preview.subject = this.preview.subject.replace(invalidRegex, '<span class="has-text-danger">$&</span>');
        }
        if (this.preview.content) {
          this.preview.content = this.preview.content.replace(invalidRegex, '<span class="has-text-danger">$&</span>');
        }
      });
    }
  }

  validateTemplateVariables(): string[] {
    const text = (this.filterForm.value.emailSubject || '') + ' ' + (this.filterForm.value.emailTextForm || '');
    const regex = /\{\{([^{}]+?)\}\}/g;
    let match;
    const invalidKeys: string[] = [];
    const validVariables = this.emailTriggerVariables.map(trigger => <string>trigger.id);
    
    while ((match = regex.exec(text)) !== null) {
      const cleanMatch = `{{${match[1].trim()}}}`;
      if (!validVariables.includes(cleanMatch)) {
        if (!invalidKeys.includes(match[0])) {
          invalidKeys.push(match[0]);
        }
      }
    }
    return invalidKeys;
  }

  getDefaultEmailTemplate(trigger): void {
    this.changeType = true;
    this.eventsService.getEventNotificationEmail(trigger)
      .subscribe(nf => {
        this.emailExists = false;
        this.notificationEmail = nf;

        this.filterForm.patchValue({
          emailSubject: nf.subject,
          emailTextForm: nf.content
        });
      });
  }
}
